// What every library page shares: an item's picture, where a version came from, and what to say when a file can't go.
import type { components } from '@tune/shared';
import { ApiError, problem } from '../../api';
import { cx } from '../../ui';
import { CpuIcon, PaletteIcon } from '../../ui/icons';
import { NotAnItem } from '../../upload';
import s from './Library.module.css';

type Origin = components['schemas']['Origin'];

/** Where a version came from, as the library says it. */
export const ORIGINS: Readonly<Record<Origin, string>> = {
  tune: 'From TUNE',
  mfd: 'From a TUNE MFD',
  web: 'From the web',
  editor: "From TUNE's theme editor",
};

/** A dashboard's thumbnail, or a tile for a config or a dashboard without one. */
export function ItemThumb({
  url,
  kind,
  size = 'regular',
}: {
  url: string | null;
  kind: 'dashboard' | 'config';
  size?: 'regular' | 'large';
}) {
  if (url) {
    return <img className={cx(s.thumb, size === 'large' && s.thumbLarge)} src={url} alt="" loading="lazy" />;
  }
  const Icon = kind === 'dashboard' ? PaletteIcon : CpuIcon;
  return (
    <span className={cx(s.thumb, s.thumbEmpty, size === 'large' && s.thumbLarge)} aria-hidden="true">
      <Icon size={size === 'large' ? 40 : 28} />
    </span>
  );
}

/** Why a file didn't go to the library, in words. */
export function uploadProblem(error: unknown): string {
  if (error instanceof NotAnItem) {
    return error.message;
  }
  if (error instanceof ApiError) {
    if (error.code === 'invalid_file') {
      return "TUNE's cloud couldn't read it as a dashboard or config TUNE Live takes, or it isn't the item it says.";
    }
    if (error.code === 'quota_exceeded') {
      return 'Your library is full. Delete something from it to add more.';
    }
    if (error.status === 413) {
      return 'That file is too big: a dashboard is 128 MB at most, and a config 2 MB.';
    }
    if (error.code === 'identity_taken') {
      return 'Another item of yours took its place meanwhile. Try again.';
    }
    if (error.code === 'part_refused' || error.code === 'offline') {
      return "Part of it didn't arrive. Check the connection, then go on.";
    }
  }
  return problem(error);
}
